import styled from "@emotion/styled";
import dayjs from "dayjs";
import React, { useEffect, useRef, useState } from "react";
import { Draggable } from "react-beautiful-dnd";
import { IoIosCheckmark } from "react-icons/io";
import { Select, MenuItem, TextField, Tooltip } from "@mui/material";
import high from "../../../../components/assets/high_priority.svg";
import low from "../../../../components/assets/low_priority.svg";
import normal from "../../../../components/assets/normal_priority.svg";
import { useNavigate } from "react-router-dom";
import { UPDATE_TASK } from "../../../../components/utils";
import { patchRequest } from "../../../../components/axiosClient";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { DatePicker } from "@mui/x-date-pickers";
import CommonDatePicker from "../../../CommonDatePicker";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import DescriptionIcon from "@mui/icons-material/Description";

const TaskInformation = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-start;
  padding: 15px;
  min-height: 106px;
  border-radius: 8px;
  background: white;
  margin-top: 15px;
  gap: 0.75rem;
  box-sizing: border-box !important;
  border-style: solid;
  border-width: 1px;
  border-color: white;

  &:hover {
    border-color: #c9c4c4;
  }

  .secondary-details {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    font-size: 12px;
    font-weight: 400px;
    color: #7d7d7d;
  }
`;

const StyledTextField = styled(TextField)`
  .MuiInput-root {
    padding: 0;
    &:before,
    &:after {
      display: none;
    }
  }
  .MuiInputBase-input {
    padding: 0;
    font-size: 14px;
  }
`;

const StyledSelect = styled(Select)`
  &.MuiSelect-root {
    padding: 0;
    color: #7d7d7d;
  }

  .MuiSelect-select {
    padding: 0;
    // padding-right: 24px !important;
    background-color: #9885C2
    color:"white"
    &:focus {
      background-color: white !important;
      background-color: #9885C2
    }
  }

  &:before,
  &:after {
    display: none;
  }

  &:hover:not(.Mui-disabled):before {
    border-bottom: none;
  }
`;

const PriorityMenuItem = styled(MenuItem)`
  display: flex;
  align-items: center;
  gap: 8px;

  img {
    width: 14px;
    height: 14px;
  }
`;

const TaskCard = ({
  item,
  index,
  onDoubleClick,
  handleTaskComplete,
  getTaskList,
  assignToFilterOption,
}) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [title, setTitle] = useState(item?.title);
  const titleRef = useRef(null);
  const customIcons = {
    Low: <img src={low} alt="Low" style={{ width: "14px", height: "14px" }} />,
    Medium: (
      <img
        src={normal}
        alt="Medium"
        style={{ width: "14px", height: "14px" }}
      />
    ),
    High: (
      <img src={high} alt="High" style={{ width: "14px", height: "14px" }} />
    ),
  };

  const priorityOptionsList = ["Low", "Medium", "High"];

  const priorityOptions = priorityOptionsList.map((priority) => ({
    label: priority,
    value: priority,
    icon: customIcons[priority],
  }));

  useEffect(() => {
    if (isEditing && titleRef.current) {
      titleRef.current.focus();
    }
  }, [isEditing]);

  const handleTitleChange = async (newTitle) => {
    if (newTitle.trim() === item.title) {
      setIsEditing(false);
      return;
    }

    try {
      await patchRequest(
        `${UPDATE_TASK}`,
        {
          title: newTitle.trim(),
          task_id: item.id,
        },
        navigate
      );
      await getTaskList();
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating title:", error);
      setTitle(item.title);
      setIsEditing(false);
    }
  };

  const handleAssigneeChange = async (newAssignee) => {
    try {
      await patchRequest(
        `${UPDATE_TASK}`,
        {
          assigned_to: newAssignee,
          task_id: item.id,
        },
        navigate
      );
      getTaskList();
    } catch (error) {
      console.error("Error updating assignee:", error);
    }
  };

  const handlePriorityChange = async (newPriority) => {
    try {
      await patchRequest(
        `${UPDATE_TASK}`,
        {
          priority: newPriority,
          task_id: item.id,
        },
        navigate
      );
      getTaskList();
    } catch (error) {
      console.error("Error updating priority:", error);
    }
  };
  let priorityClass = "";

  if (item?.priority === "Low") {
    priorityClass = "bg-[#a1a8b0]";
  } else if (item?.priority === "Medium") {
    priorityClass = "bg-[#95ccff]";
  } else if (item?.priority === "High") {
    priorityClass = "bg-[#ff9099]";
  }

  // console.log(item);

  return (
    <Draggable key={item.id} draggableId={item.id.toString()} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={onDoubleClick}
          className="!cursor-pointer border-0 !border-black hover:border-1"
        >
          <TaskInformation
            className={`${
              item?.member_removed === "true"
                ? "!bg-[#fbebeb] border border-[#f1c1c1]"
                : ""
            }  ${item?.is_completed ? "opacity-60" : "opacity-100"} `}
          >
            <div className="flex  gap-2 w-full">
              <div className=" mt-1">
                {" "}
                <div
                  className={`rounded-full border flex items-center 
                  ${
                    !item?.is_completed
                      ? "bg-white text-black"
                      : "bg-green-600 text-white"
                  } cursor-pointer`}
                  onClick={async (e) => {
                    e.stopPropagation();
                    await handleTaskComplete(item.id, item?.is_completed);
                    await getTaskList();
                  }}
                >
                  <IoIosCheckmark />
                </div>
              </div>

              <div
                className="w-full"
                onClick={(e) => {
                  e.stopPropagation();
                  setIsEditing(true);
                }}
              >
                {isEditing ? (
                  <StyledTextField
                    inputRef={titleRef}
                    variant="standard"
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={4.5}
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={() => handleTitleChange(title)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        handleTitleChange(title);
                      } else if (e.key === "Escape") {
                        setTitle(item.title);
                        setIsEditing(false);
                      }
                    }}
                  />
                ) : (
                  <p className="w-[95%] max-h-[104px] overflow-hidden text-ellipsis text-justify text-sm">
                    {item?.title}
                  </p>
                  // <p className="truncate w-[95%]">{item?.title}</p>
                )}
                {/* <p>{item.titile}</p> */}
              </div>
              <div>
                {item?.description && (
                  <DescriptionIcon fontSize="1px" sx={{ color: "green" }} />
                )}
              </div>
            </div>
            <div className=" flex  justify-start gap-1 items-center w-full text-sm text-[#7D7D7D]">
              <div
                className={`text-[12px] text-black flex justify-center align-middle text-center pr-[4px] pl-[4px] border rounded-md ${priorityClass}`}
              >
                {item?.priority}
              </div>
              <div>
                {item.tags.length > 0 &&
                  item.tags.map((tag, idx) => (
                    <>
                      {idx < 4 && (
                        <Tooltip title={tag}>
                          <LocalOfferIcon
                            sx={{ fontSize: "17px", color: "#6ec38e" }}
                          />
                        </Tooltip>
                      )}
                    </>
                  ))}
              </div>
              {/* <div>{item.tags.length > 0 && <LocalOfferIcon />}</div> */}
            </div>

            <div className="flex justify-between items-center gap-2 w-full text-sm text-[#7D7D7D]">
              <div
                className="flex items-center gap-1"
                onClick={(e) => e.stopPropagation()}
              >
                <StyledSelect
                  value={item?.assignee?.id || ""}
                  onChange={(e) => handleAssigneeChange(e.target.value)}
                  displayEmpty
                  variant="standard"
                  IconComponent={null}
                  renderValue={(selected) => {
                    const selectedMember = assignToFilterOption.find(
                      (member) => member.value === selected
                    );
                    if (selectedMember) {
                      const [firstName, lastName] =
                        selectedMember.label.split(" ");
                      return (
                        <Tooltip
                          title={selectedMember.label}
                          placement="bottom"
                        >
                          {firstName?.charAt(0).toUpperCase()}
                          {lastName ? lastName.charAt(0).toUpperCase() : ""}
                        </Tooltip>
                      );
                    }
                    return <AccountCircleIcon />;
                  }}
                  sx={{
                    border: "0.5px dotted #9885C2",
                    borderRadius: "50%",
                    backgroundColor: "#9885C2",
                    color: "white",
                    "& .MuiSelect-select.MuiInputBase-input.MuiInput-input": {
                      paddingRight: "0px",
                      minWidth: "16px",
                      width: "20px",
                      height: "20px",
                      border: "0.5px dotted #9885C2",
                      borderRadius: "50%",
                      display: "flex",
                      justifyContent: "center",
                      fontSize: "11px",
                      backgroundColor: "#9885C2",
                      color: "white",
                      alignItems: "center",
                    },
                  }}
                >
                  {assignToFilterOption.map((member) => (
                    // console.log(member),
                    <MenuItem key={member.value} value={member.value}>
                      {member.label}
                    </MenuItem>
                  ))}
                </StyledSelect>
                <span>
                  <Tooltip title="due date" placement="bottom">
                    {item?.due_date && dayjs(item?.due_date).format("MMM D")}
                  </Tooltip>
                </span>
              </div>

              <span>
                <Tooltip title="created date" placement="bottom">
                  {item?.created_at && dayjs(item?.created_at).format("MMM D")}
                </Tooltip>
              </span>
            </div>
          </TaskInformation>
        </div>
      )}
    </Draggable>
  );
};

export default TaskCard;
